import React, { useState } from "react";
import { Box, Button, Tooltip } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";

interface InviteShareButtonsProps {
  token: string;
  circleTitle: string;
  size?: "small" | "medium";
}

// WhatsApp / SMS / copy sharing for one invite link. The link points at the
// public landing page (/join/<token>) which previews the circle and routes
// the invitee into signup. WhatsApp first — it's how these circles organize.
const InviteShareButtons = ({
  token,
  circleTitle,
  size = "small",
}: InviteShareButtonsProps) => {
  const [copied, setCopied] = useState(false);

  const link = `${window.location.origin}/join/${token}`;
  const message = `Join my circle "${circleTitle}" on Sanduuq: ${link}`;
  const encoded = encodeURIComponent(message);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(link);
    } catch {
      // Clipboard API needs a secure context; fall back to a prompt.
      window.prompt("Copy this invite link:", link);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const btnSx = {
    textTransform: "none" as const,
    fontWeight: 600,
    fontSize: size === "small" ? "0.72rem" : "0.82rem",
    borderRadius: "8px",
    px: 1.25,
    py: 0.4,
    minWidth: 0,
  };

  return (
    <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
      <Button
        size={size}
        variant="outlined"
        color="success"
        startIcon={<WhatsAppIcon sx={{ fontSize: "0.95rem !important" }} />}
        href={`https://wa.me/?text=${encoded}`}
        target="_blank"
        rel="noopener noreferrer"
        sx={btnSx}
      >
        WhatsApp
      </Button>
      <Button
        size={size}
        variant="outlined"
        startIcon={<SmsOutlinedIcon sx={{ fontSize: "0.95rem !important" }} />}
        // The lone "?&" is the one form both iOS and Android accept.
        href={`sms:?&body=${encoded}`}
        sx={btnSx}
      >
        SMS
      </Button>
      <Tooltip title={copied ? "Copied!" : "Copy link"} placement="top">
        <Button
          size={size}
          variant="outlined"
          color={copied ? "success" : "inherit"}
          startIcon={
            copied ? (
              <CheckIcon sx={{ fontSize: "0.95rem !important" }} />
            ) : (
              <ContentCopyIcon sx={{ fontSize: "0.95rem !important" }} />
            )
          }
          onClick={handleCopy}
          sx={{ ...btnSx, color: copied ? undefined : "text.secondary", borderColor: "divider" }}
        >
          {copied ? "Copied" : "Copy"}
        </Button>
      </Tooltip>
    </Box>
  );
};

export default InviteShareButtons;
